import { ChangeDetectionStrategy, Component, Input, OnInit, ViewEncapsulation } from '@angular/core';
import { ObjectTypes } from '@fi-shared/types';
import { fiTextToHtml, getObjectTypeNameTextId } from '@fi-shared/utils';
import { AppTextService } from '@fi-shared/app-text';
import { AccessRightService } from '@fi-shared/fi-shared-services';
import { LoadingService } from '@fi-shared/fi-loading/loading.service';
import {
    IExtendSharingModel,
    IExtendSharingObject,
    IExtendSharingObjectItem,
    IExtendSharingResult,
} from '@fi-app/components/extend-sharing/extend-sharing';
import { ExtendSharingService } from '@fi-app/components/extend-sharing/extend-sharing.service';
import { AccessRightFilterCollectionModel, IAccessRightCoreFilter, ILightWeightObject } from '@fi-shared/models';
import { BehaviorSubject, ReplaySubject } from 'rxjs';
import { FiStateService } from '@fi-shared/fi-state-proxy/fi-state.proxy.service';
import { FiStateNames } from '@fi-shared/fi-state-proxy/fi-state-names';
import { FiAccessRightHelperService } from '@fi-shared/fi-shared-services/fi-access-right-helper.service';
import { FiDialogsService } from '@fi-shared/fi-dialogs';
import { FiWarningConfirmDialogComponent } from '@fi-shared/fi-dialogs-generic/warning-confirm/fi-warning-confirm-dialog.component';
import { FiCustomerDataService } from '@fi-shared/fi-shared-services/fi-customer-data.service';

@Component({
    selector: 'fi-extend-sharing-content',
    templateUrl: './extend-sharing-content.component.html',
    styleUrls: ['./extend-sharing-content.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { class: 'fi-extend-sharing-content' },
    standalone: false,
})
export class ExtendSharingContentComponent implements OnInit {
    @Input() objectId: number;
    @Input() objectType: ObjectTypes;
    @Input() objectName: string;

    loading$ = new ReplaySubject<boolean>();
    isSharingChanged$ = new BehaviorSubject<boolean>(true);
    signText: string;

    objectTypesSelection: IExtendSharingObjectItem[] = [];

    extendSharingObjects: IExtendSharingObject[] = [];

    extendSharingSummary$ = new ReplaySubject<IExtendSharingResult[]>(1);

    constructor(
        private extendSharingService: ExtendSharingService,
        private appTextService: AppTextService,
        private accessRightService: AccessRightService,
        private fiLoading: LoadingService,
        private stateService: FiStateService,
        private customerDataService: FiCustomerDataService,
        private fiAccessRightHelperService: FiAccessRightHelperService,
        private fiDialogsService: FiDialogsService,
    ) {}

    ngOnInit(): void {
        this.loading$.next(true);
        this.fiLoading
            .trackPromise(this.accessRightService.GetAccessRightsForUser())
            .then((result: AccessRightFilterCollectionModel) => this.setExtendSharingObjects(result))
            .finally(() => this.loading$.next(false));
    }

    resetSummary() {
        this.isSharingChanged$.next(true);
        this.extendSharingSummary$.next(undefined);
        this.signText = '';
    }

    onExtendSharingClick() {
        this.fiDialogsService
            .custom({
                contentComponentRef: FiWarningConfirmDialogComponent,
                contentComponentData: {
                    message: this.appTextService.get('extend_sharing.confirm_dialog_message'),
                    warningMessage: this.appTextService.get('extended_sharing.warning_message'),
                    confirmText: this.appTextService.get('common.confirm').toUpperCase(),
                },
                yesButtonText: this.appTextService.get('common.confirm'),
                noButtonText: this.appTextService.get('common.cancel'),
                isTopCloseButtonShown: true,
                colorClass: 'orange',
                config: {
                    panelClass: 'fi-rounded-dialog-panel',
                    width: '540px',
                    maxWidth: '90%',
                },
            })
            .onYesButtonClick(() => {
                const extendSharingModel: IExtendSharingModel = this.getExtendSharingModel();
                this.fiLoading
                    .trackPromise(this.extendSharingService.makeExtendSharing(extendSharingModel))
                    .then(() => {
                        this.goToListPage();
                    });
            });
    }

    onGetSummaryClick() {
        const extendSharingModel: IExtendSharingModel = this.getExtendSharingModel();
        this.fiLoading
            .trackPromise(this.extendSharingService.getExtendSharingSummary(extendSharingModel))
            .then((result: IExtendSharingResult[]) => {
                this.extendSharingSummary$.next(result);
                this.isSharingChanged$.next(false);
            });
    }

    getTextWithObjectName(label: string): string {
        return this.appTextService.get(label, {
            '{0}': fiTextToHtml(this.objectName),
        });
    }

    getSummaryItemName(extendSharingSummaryItem: IExtendSharingResult): string {
        return this.getObjectTypeName(extendSharingSummaryItem.ObjectType, extendSharingSummaryItem.ItemsCount !== 1);
    }

    getSummaryDescriptionText(): string {
        const selectedObjectTypes: IExtendSharingObjectItem[] = this.objectTypesSelection.filter(
            (objectItem: IExtendSharingObjectItem) => objectItem.isChecked,
        );

        const objectTypeName = this.appTextService.get(getObjectTypeNameTextId(this.objectType, true));

        return this.appTextService.get('extended_sharing.summary_description', {
            '{selected_object_types}': selectedObjectTypes
                .map((objectItem: IExtendSharingObjectItem) => {
                    return objectItem.label;
                })
                .join(', ')
                .toLowerCase(),
            '{object_type_name}': objectTypeName.toLowerCase(),
            '{object_name}': fiTextToHtml(this.objectName),
        });
    }

    isProceedButtonDisabled(): boolean {
        return (
            this.objectTypesSelection.every((objectTypeSelection: IExtendSharingObjectItem) => {
                return !objectTypeSelection.isChecked;
            }) || !this.getAlsoSharedSelectedItems().length
        );
    }

    private getAlsoSharedSelectedItems(): number[] {
        const extendSharingModel: IExtendSharingModel = this.getExtendSharingModel();
        switch (this.objectType) {
            case ObjectTypes.Area:
                return extendSharingModel.AlsoSharedWithAreaIds;
            case ObjectTypes.BusinessArea:
                return extendSharingModel.AlsoSharedWithBusinessAreaIds;
            case ObjectTypes.Role:
                return extendSharingModel.AlsoSharedWithRoleIds;
            default:
                return [];
        }
    }

    private setExtendSharingObjects(result: AccessRightFilterCollectionModel) {
        this.objectTypesSelection = [
            {
                objectType: ObjectTypes.Document,
                label: this.getObjectTypeName(ObjectTypes.Document, true),
                isChecked: true,
            },
            {
                objectType: ObjectTypes.MediaBank,
                label: this.getObjectTypeName(ObjectTypes.MediaBank, true),
                isChecked: true,
            },
            { objectType: ObjectTypes.Post, label: this.getObjectTypeName(ObjectTypes.Post, true), isChecked: true },
            {
                objectType: ObjectTypes.CheckList,
                label: this.getObjectTypeName(ObjectTypes.CheckList, true),
                isChecked: true,
            },
            { objectType: ObjectTypes.Forum, label: this.getObjectTypeName(ObjectTypes.Forum, true), isChecked: true },
            { objectType: ObjectTypes.Form, label: this.getObjectTypeName(ObjectTypes.Form, true), isChecked: true },
        ];

        if (this.showChecklistsV2()) {
            this.objectTypesSelection.push({
                objectType: ObjectTypes.Checklist2,
                label: this.getObjectTypeName(ObjectTypes.Checklist2, true),
                isChecked: true,
            });
        }
        if (this.showChecklistSchedulesV2()) {
            this.objectTypesSelection.push({
                objectType: ObjectTypes.ChecklistSchedule,
                label: this.getObjectTypeName(ObjectTypes.ChecklistSchedule, true),
                isChecked: true,
            });
        }

        if (this.showChecklistsV2()) {
            this.objectTypesSelection.push({
                objectType: ObjectTypes.Checklist2Audit,
                label: this.getObjectTypeName(ObjectTypes.Checklist2Audit, true),
                isChecked: true,
            });
        }
        if (this.showChecklistSchedulesV2()) {
            this.objectTypesSelection.push({
                objectType: ObjectTypes.Checklist2AuditSchedule,
                label: this.getObjectTypeName(ObjectTypes.Checklist2AuditSchedule, true),
                isChecked: true,
            });
        }

        this.extendSharingObjects.push(this.getSharingObjectByObjectType(result.AccessRightCoreFilter));
    }

    private showChecklistsV2() {
        // checklists v2 have no roles selection
        return this.objectType === ObjectTypes.Area || this.objectType === ObjectTypes.BusinessArea;
    }

    private showChecklistSchedulesV2() {
        return true;
    }

    private getSharingObjectByObjectType(accessRightCoreFilter: IAccessRightCoreFilter): IExtendSharingObject {
        switch (this.objectType) {
            case ObjectTypes.Role:
                return {
                    objectType: ObjectTypes.Role,
                    items: this.getAlsoSharedData(accessRightCoreFilter.Roles, ObjectTypes.Role),
                    headerText: this.appTextService.get('extended_sharing.role_header'),
                    descriptionText: this.getTextWithObjectName('extended_sharing.role_description'),
                };
            case ObjectTypes.BusinessArea:
                return {
                    objectType: ObjectTypes.BusinessArea,
                    items: this.getAlsoSharedData(accessRightCoreFilter.BusinessAreas, ObjectTypes.BusinessArea),
                    headerText: this.appTextService.get('extended_sharing.business_area_header'),
                    descriptionText: this.getTextWithObjectName('extended_sharing.business_area_description'),
                };
            case ObjectTypes.Area:
                const allAreas: ILightWeightObject[] = this.fiAccessRightHelperService.getLightWeightObjectsFromAreas(
                    accessRightCoreFilter.Areas,
                );
                return {
                    objectType: ObjectTypes.Area,
                    items: this.getAlsoSharedData(allAreas, ObjectTypes.Area),
                    headerText: this.appTextService.get('extended_sharing.area_header'),
                    descriptionText: this.getTextWithObjectName('extended_sharing.area_description'),
                };
        }
        throw new Error(`getSharingObjectByObjectType: unknown object type ${this.objectType}`);
    }

    private getExtendSharingModel(): IExtendSharingModel {
        return {
            ObjectId: this.objectId,
            ObjectType: this.objectType,
            TargetObjectTypes: this.objectTypesSelection
                .filter((item) => item.isChecked)
                .map((item) => item.objectType),
            AlsoSharedWithRoleIds: !this.isRoleObjectType()
                ? []
                : this.extendSharingObjects
                      .find((object) => object.objectType === ObjectTypes.Role)
                      ?.items.filter((object) => object.isChecked)
                      .map((item) => item.id),
            AlsoSharedWithBusinessAreaIds:
                this.objectType !== ObjectTypes.BusinessArea
                    ? []
                    : this.extendSharingObjects
                          .find((object) => object.objectType === ObjectTypes.BusinessArea)
                          ?.items.filter((object) => object.isChecked)
                          .map((item) => item.id),
            AlsoSharedWithAreaIds:
                this.objectType !== ObjectTypes.Area
                    ? []
                    : this.extendSharingObjects
                          .find((object) => object.objectType === ObjectTypes.Area)
                          ?.items.filter((object) => object.isChecked)
                          .map((item) => item.id),
            SignText: this.signText,
        };
    }

    private getAlsoSharedData(items: ILightWeightObject[], objectType: ObjectTypes): IExtendSharingObjectItem[] {
        // we should exclude current item
        return items
            .filter((item) => {
                return !(this.objectType === objectType && item.Id === this.objectId);
            })
            .map((item) => ({
                id: item.Id,
                label: item.Name,
                isChecked: false,
            }));
    }

    private goToListPage() {
        switch (this.objectType) {
            case ObjectTypes.BusinessArea:
                return this.stateService.go(FiStateNames.BusinessArea.List);
            case ObjectTypes.Area:
                return this.stateService.go(FiStateNames.Area.List);
            case ObjectTypes.Role:
                return this.stateService.go(FiStateNames.Role.List);
        }
    }

    getObjectTypeName(objectType: ObjectTypes, isPlural: boolean = true): string {
        return this.appTextService.get(getObjectTypeNameTextId(objectType, isPlural));
    }

    isRoleObjectType(): boolean {
        return this.objectType === ObjectTypes.Role;
    }

    getExtendSharingHeading(): string {
        if (!this.isRoleObjectType()) return this.appTextService.get('extended_sharing.select_content_type_main');
        return this.appTextService.get('extended_sharing.select_content_type_main_roles', {
            '{0}': this.objectName,
        });
    }
}
